import React, { Dispatch, forwardRef, SetStateAction, useState } from 'react';
import classes from './SocialProfile.module.scss';
import { ReactComponent as DefaultImage } from '../../../assets/images/defaultProfileImage.svg';
import { SocialNetwork } from '../SocialNetwork';
import classNames from 'classnames';
import { capitalizeString } from '../../../helpers/capitalizeString';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { Checkbox } from '../../forms/Checkbox';
import { FormElementValueType } from '../../../models/basic-objects';
import inputClasses from '../../forms/form.module.scss';
import { PinterestBoard } from '../PinterestBoard';
import {
  CorePinterestBoardFieldsFragment,
  GenericProfileType,
  Network,
  SocialOAuthProvider
} from '../../../models/graphql';
import { Tooltip } from '../../actions/Tooltip';
import uuidGen from '../../../helpers/uuidGen';
import { Debugger } from '../../containers/Debugger';
import { ErrorRender } from '../../containers/ErrorRender';
import { TikTokProfileValues, TikTokProfileSettingsType } from '../SocialProfileListItem/SocialProfileListItem.view';
import { TikTokSettings } from '../TikTokSettings';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { Link } from 'react-router-dom';

export type SocialProfileComponentExtendedType = SocialProfileComponentBasicType & {
  type: string;
  postAs?: string;
  pinterestBoards?: CorePinterestBoardFieldsFragment[];
  tikTokSettings?: TikTokProfileSettingsType;
};

export type SocialProfileComponentBasicType = {
  id: string;
  network: Network;
  provider: SocialOAuthProvider | null;
  genericProfile?: GenericProfileType;
  name: string;
  image?: string;
};

export type NoPinterestBoardType = {
  pinterestBoard?: false;
  pinterestFormName?: never;
  pinterestBoardValue?: never;
  pinterestRules?: never;
};

export type HasPinterestBoardType = {
  pinterestBoard: true;
  pinterestFormName: string;
  pinterestBoardValue?: FormElementValueType;
  pinterestRules: RegisterOptions;
};

export type NoTikTokSettingsType = {
  tikTokSettings?: false;
  tikTokFormName?: never;
  tikTokTargetValue?: never;
  tikTokActionValue?: never;
  tikTokPromotionValue?: never;
  tikTokTargetRules?: never;
  tikTokActionRules?: never;
  tikTokPromotionRules?: never;
};

export type HasTikTokSettingsType = {
  tikTokSettings: true;
  tikTokFormName: string;
  tikTokTargetValue?: TikTokProfileValues['privacyTarget'];
  tikTokActionValue?: TikTokProfileValues['allowedActions'];
  tikTokPromotionValue?: TikTokProfileValues['promotion'];
  tikTokTargetRules: RegisterOptions;
  tikTokActionRules: RegisterOptions;
  tikTokPromotionRules: RegisterOptions;
};

export type PinterestProfileType = NoPinterestBoardType | HasPinterestBoardType;
export type TikTokSettingsType = NoTikTokSettingsType | HasTikTokSettingsType;

export type SocialProfileLabelsType =
  | {
      label?: boolean;
      sublabel?: never;
      sublabelText?: never;
      socialProfile: SocialProfileComponentBasicType;
    }
  | {
      label: boolean;
      sublabel?: boolean;
      sublabelText?: string;
      socialProfile: SocialProfileComponentExtendedType;
    };

export type SocialProfileCheckboxType =
  | {
      checkbox?: false;
      useFormName?: never;
      rules?: never;
      useFormInstance?: never;
      value?: never;
      checked?: never;
    }
  | ({
      checkbox: true;
      useFormName: string;
      rules: RegisterOptions;
      useFormInstance: UseFormReturn;
      value?: string;
      checked?: true;
    } & PinterestProfileType &
      TikTokSettingsType);

export type SocialProfileViewProps = SocialProfileLabelsType &
  SocialProfileCheckboxType & {
    //
    disabled?: boolean;
    disconnected?: boolean;
    image?: string;
    loading?: boolean;
    size: 'small' | 'medium' | 'large';
    variant?: 'white' | 'default';
    getSublabel: (socialProfile: SocialProfileComponentExtendedType) => string;
    showPinterestBoard: boolean;
    showTikTokSettings: boolean;
    setShowTikTokSettings: Dispatch<SetStateAction<boolean>>;
    dropdownRef?: any;
    onCheck: (checked: boolean) => void;
    isParentDropdownExpanded?: boolean;
  };

const isSocialProfileComponentExtendedTypeGuard = (
  value: SocialProfileComponentBasicType | SocialProfileComponentExtendedType
): value is SocialProfileComponentExtendedType => 'type' in value;

export const SocialProfileView = forwardRef<HTMLDivElement | null, SocialProfileViewProps>(
  (props, ref): JSX.Element => {
    const globalClasses = classNames('grid-x align-middle', classes.SocialProfile, {
      [classes.Disabled]: props.disabled || props.loading
    });
    const imageClasses = classNames(classes.ImageWrapper, classes[`ImageWrapper${capitalizeString(props.size)}`]);
    const iconWrapperClasses = classNames(classes.IconWrapperClasses);
    const imageWrapperClasses = classNames(classes.ImageWrapperClasses, {
      [classes.ImageWrapperClassesDisconnected]: props.disconnected,
      [classes[`ImageWrapperClassesDisconnected${capitalizeString(props.size)}`]]: props.disconnected,
      [classes.DisabledImage]: props.disabled,
      [classes[`DisabledImage${capitalizeString(props.size)}`]]: props.disabled
    });
    const labelClasses = classNames(classes.Label, 'no-b-m', props.variant === 'white' ? classes.LabelWhite : null);
    const errorClasses = classNames(inputClasses.HelpTextErrorClass, inputClasses.HelpText);

    const [tooltipId] = useState<string>(uuidGen());

    const checkbox = props.checkbox ? (
      <div className={'cell shrink'}>
        <div className={classes.CheckboxWrapper}>
          <Checkbox
            hideError={true}
            formInstance={props.useFormInstance}
            rules={props.rules}
            size={'small'}
            action={props.onCheck}
            readonly={props.disabled || props.loading}
            {...(typeof props.checked === 'boolean' ? { checked: props.checked } : {})}
            {...(props.disabled && !props.loading
              ? { name: `ignored_${props.useFormName}` }
              : { name: props.useFormName })}
            {...(props.value ? { value: props.value } : {})}
          />
        </div>
      </div>
    ) : null;

    const imageComponent: JSX.Element = props.image ? (
      <img src={props.image} alt={props.label ? props.socialProfile.name : ''} />
    ) : (
      <DefaultImage />
    );
    const image = (
      <>
        <div className={'cell shrink'}>
          <div className={imageClasses}>
            <Debugger tooltipContent={`Profile ID: ${props.socialProfile.id}`} modalContent={props.socialProfile} />
            <div className={imageWrapperClasses}>{imageComponent}</div>
            <div className={iconWrapperClasses}>
              <SocialNetwork
                type={'circle'}
                provider={props.socialProfile.provider}
                network={props.socialProfile.network}
                genericProfile={props.socialProfile.genericProfile}
                size={props.size}
                disabled={props.disabled}
              />
            </div>
          </div>
        </div>
      </>
    );

    const labels = props.label ? (
      <div className={'cell auto'}>
        <p className={labelClasses}>{props.socialProfile.name}</p>
        {props.size !== 'small' && props.sublabel ? (
          <>
            <span className={classNames(classes.Sublabel, props.variant === 'white' ? classes.SublabelWhite : null)}>
              {props.sublabelText || props.getSublabel(props.socialProfile)}
            </span>
          </>
        ) : null}
      </div>
    ) : null;

    const error = !!props.checkbox ? (
      <ErrorRender errors={props.useFormInstance.formState.errors} name={props.useFormName} className={errorClasses} />
    ) : (
      <></>
    );

    const handleLabelClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const { useFormInstance, useFormName, value } = props;
      const currentValue = useFormInstance?.getValues(useFormName);
      const valueToSet = !currentValue ? value : false;
      useFormInstance?.setValue(useFormName, valueToSet);
      useFormInstance?.trigger(useFormName);
      props.onCheck(!!valueToSet);
    };

    const profile = props.socialProfile;

    return (
      <>
        <Tooltip
          type={'regular'}
          position={props.disconnected ? 'right' : 'bottom'}
          pointer={true}
          autoZIndex={false}
          style={{ zIndex: scssVars['z-index-overmodal'] }}
          autoHide={!props.disconnected}
          target={`[data-tooltip-id="${tooltipId}"]`}>
          {props.disconnected ? (
            <>
              <span>
                {props.socialProfile.name} is disconnected, go to the Social Profiles page to{' '}
                <Link to={'/settings/profiles'}>reconnect</Link> it
              </span>
            </>
          ) : (
            <> {props.socialProfile.name}</>
          )}
        </Tooltip>
        <div
          className={globalClasses}
          ref={ref}
          {...(!props.label || props.disconnected ? { 'data-tooltip-id': tooltipId } : {})}>
          {checkbox}
          <div className={'cell auto'}>
            <div
              onClick={props.checkbox ? handleLabelClick : undefined}
              className={classNames('grid-x align-middle', props.checkbox ? classes.SocialProfileClickableLabel : '')}>
              {image}
              {labels}
            </div>
          </div>
          {!!props.checkbox &&
          props.socialProfile.provider === SocialOAuthProvider.PINTEREST &&
          isSocialProfileComponentExtendedTypeGuard(props.socialProfile) &&
          props.pinterestBoard &&
          props.showPinterestBoard ? (
            <PinterestBoard
              parentDropdownRef={props.dropdownRef}
              pinterestBoardValue={props.pinterestBoardValue}
              socialProfile={props.socialProfile}
              useFormInstance={props.useFormInstance}
              pinterestFormName={props.pinterestFormName}
              pinterestRules={props.pinterestRules}
              isParentDropdownExpanded={!!props.isParentDropdownExpanded}
            />
          ) : null}
          {!!props.checkbox &&
          props.tikTokSettings &&
          props.socialProfile.provider === SocialOAuthProvider.TIKTOK &&
          isSocialProfileComponentExtendedTypeGuard(profile) &&
          profile.tikTokSettings ? (
            <TikTokSettings
              checked={props.checked}
              disabled={props.disabled}
              tikTokSettings={profile.tikTokSettings}
              profileId={profile.id}
              useFormInstance={props.useFormInstance}
              tikTokFormName={props.tikTokFormName}
              tikTokTargetValue={props.tikTokTargetValue}
              tikTokTargetRules={props.tikTokTargetRules}
              tikTokActionValue={props.tikTokActionValue}
              tikTokActionRules={props.tikTokActionRules}
              tikTokPromotionValue={props.tikTokPromotionValue}
              tikTokPromotionRules={props.tikTokPromotionRules}
              expanded={props.showTikTokSettings}
              setExpanded={props.setShowTikTokSettings}
              dropdownRef={props.dropdownRef}
              isParentDropdownExpanded={!!props.isParentDropdownExpanded}
            />
          ) : null}
        </div>
        {error}
      </>
    );
  }
);

SocialProfileView.displayName = 'SocialProfileView';
